/**
 * The landing's course overview (components/home/CourseOverview.tsx): the
 * general track's worlds grouped into five stages, then the specialization
 * tracks, each with its real level count from the catalogue.
 *
 * Which world is in which group is the one hand-written fact here. A world
 * added to the course and missing from `GROUPS` would silently fall out of
 * the overview, so web/test/course-overview.test.ts holds that every world on
 * the general track is in exactly one group.
 */
import type { PublicMission } from '../types';

export type GroupId = 'start' | 'git' | 'shipping' | 'systems' | 'owning';
export type TrackId = 'ai' | 'security' | 'blobor';

export const GROUPS: { id: GroupId; worlds: string[] }[] = [
  { id: 'start', worlds: ['The Workstation', 'Real Tasks'] },
  { id: 'git', worlds: ['First Commits', 'Branches & Remotes', 'Recovery', 'Team Git', 'Code Review'] },
  { id: 'shipping', worlds: ['Quality', 'Releases', 'The Pipeline', 'Production', 'Observability', 'Configuration'] },
  { id: 'systems', worlds: ['The Program', 'The Network', 'The Database', 'The API', 'Data', 'Concurrency', 'Security', 'Scale & Cost'] },
  { id: 'owning', worlds: ['The Codebase', 'Design', 'Ownership', 'The Platform', 'Legacy', 'The Craft', 'The Engineer', 'The Whole Job'] },
];

export const TRACKS: TrackId[] = ['ai', 'security', 'blobor'];

export interface OverviewCard<Id extends string> {
  id: Id;
  /** The worlds in course order, as the catalogue names them, with only those that exist. */
  worlds: string[];
  levels: number;
}

/** The overview as the catalogue has it: groups and tracks with their levels counted, checkpoints apart. */
export function courseOverview(missions: PublicMission[]): {
  groups: OverviewCard<GroupId>[];
  tracks: OverviewCard<TrackId>[];
  checkpoints: number;
  levels: number;
} {
  const general = missions.filter((m) => m.track === 'general');
  const groups = GROUPS.map((g) => {
    const inGroup = general.filter((m) => g.worlds.includes(m.world));
    const worlds = [...new Set(inGroup.map((m) => m.world))];
    return { id: g.id, worlds, levels: inGroup.length };
  }).filter((g) => g.levels > 0);
  const tracks = TRACKS.map((id) => {
    const inTrack = missions.filter((m) => m.track === id);
    return { id, worlds: [...new Set(inTrack.map((m) => m.world))], levels: inTrack.length };
  }).filter((t) => t.levels > 0);
  const checkpoints = missions.filter((m) => m.track === 'checkpoint').length;
  const levels = missions.filter((m) => m.track !== 'checkpoint').length;
  return { groups, tracks, checkpoints, levels };
}

/** The general track's worlds no group names: empty while the overview covers the course. */
export function ungroupedWorlds(missions: PublicMission[]): string[] {
  const grouped = new Set(GROUPS.flatMap((g) => g.worlds));
  return [...new Set(missions.filter((m) => m.track === 'general' && !grouped.has(m.world)).map((m) => m.world))];
}
